import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Download, Loader2, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  buildExportRows,
  buildFilename,
  downloadBlob,
  toCsv,
  type ExportBooking,
  type ExportFormat,
  type ExportPreset,
} from "@/lib/bookingsExport";
import { subDays, parseISO } from "date-fns";

type Scope = "current_filters" | "all" | "last_30" | "last_365";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  businessName: string;
  /** All bookings on the active outlet for this business. Already authorised. */
  allBookings: ExportBooking[];
  /** Bookings filtered by the existing Filter panel (the "current view"). */
  filteredBookings: ExportBooking[];
};

export default function BookingsExportDialog({
  open,
  onOpenChange,
  businessId,
  businessName,
  allBookings,
  filteredBookings,
}: Props) {
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [preset, setPreset] = useState<ExportPreset>("full");
  const [scope, setScope] = useState<Scope>("current_filters");
  const [downloading, setDownloading] = useState(false);

  const rowsToExport = useMemo<ExportBooking[]>(() => {
    if (scope === "current_filters") return filteredBookings;
    if (scope === "all") return allBookings;
    const days = scope === "last_30" ? 30 : 365;
    const cutoff = subDays(new Date(), days);
    return allBookings.filter((b) => {
      try {
        return parseISO(b.booking_date) >= cutoff;
      } catch {
        return false;
      }
    });
  }, [scope, allBookings, filteredBookings]);

  const rowCount = rowsToExport.length;

  const handleClose = useCallback(
    (next: boolean) => {
      if (!next) {
        setFormat("csv");
        setPreset("full");
        setScope("current_filters");
        setDownloading(false);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  const handleExport = async () => {
    if (rowCount === 0) return;
    setDownloading(true);
    try {
      const { headers, rows } = buildExportRows(rowsToExport, preset);
      const filename = buildFilename(businessName || "business", format);

      if (format === "csv") {
        const csv = toCsv(headers, rows);
        downloadBlob(csv, filename, "text/csv;charset=utf-8");
      } else {
        const XLSX = await import("xlsx");
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        // Bold the header row (style is lost in basic SheetJS community build,
        // but we set column widths for usability).
        const colWidths = headers.map((h) => {
          const longest = rows.reduce(
            (max, r) => Math.max(max, String(r[h] ?? "").length),
            h.length,
          );
          return { wch: Math.min(longest + 2, 50) };
        });
        ws["!cols"] = colWidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Bookings");
        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        downloadBlob(
          out,
          filename,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
      }

      // Fire-and-forget audit log. Never block the download on this.
      supabase.functions
        .invoke("log-export", {
          body: {
            business_id: businessId,
            entity: "bookings",
            format,
            preset,
            scope,
            row_count: rowCount,
          },
        })
        .catch((e) => {
          console.warn("[export] audit log failed (download still succeeded)", e);
        });

      toast.success(
        `Exported ${rowCount} booking${rowCount === 1 ? "" : "s"} as ${format.toUpperCase()}`,
      );
      handleClose(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Export failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Export bookings</DialogTitle>
          <DialogDescription>
            Download your bookings as a spreadsheet file. Pick the format and
            which rows to include.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Format */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-foreground">
              Format
            </Label>
            <RadioGroup
              value={format}
              onValueChange={(v) => setFormat(v as ExportFormat)}
              className="grid grid-cols-2 gap-3"
            >
              <Label
                htmlFor="fmt-csv"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="fmt-csv" value="csv" />
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">CSV</p>
                  <p className="text-xs text-muted-foreground">
                    Opens in any spreadsheet app
                  </p>
                </div>
              </Label>
              <Label
                htmlFor="fmt-xlsx"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="fmt-xlsx" value="xlsx" />
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Excel (XLSX)</p>
                  <p className="text-xs text-muted-foreground">
                    Sized columns, single sheet
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* Scope */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-foreground">
              Which bookings
            </Label>
            <RadioGroup
              value={scope}
              onValueChange={(v) => setScope(v as Scope)}
              className="space-y-2"
            >
              <Label
                htmlFor="scope-current"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="scope-current" value="current_filters" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Match current filters</p>
                  <p className="text-xs text-muted-foreground">
                    Whatever's visible in your Bookings table right now
                  </p>
                </div>
              </Label>
              <Label
                htmlFor="scope-all"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="scope-all" value="all" />
                <div className="flex-1">
                  <p className="text-sm font-medium">All bookings on this outlet</p>
                  <p className="text-xs text-muted-foreground">
                    Every row regardless of filters
                  </p>
                </div>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Label
                  htmlFor="scope-30"
                  className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
                >
                  <RadioGroupItem id="scope-30" value="last_30" />
                  <p className="text-sm font-medium">Last 30 days</p>
                </Label>
                <Label
                  htmlFor="scope-365"
                  className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
                >
                  <RadioGroupItem id="scope-365" value="last_365" />
                  <p className="text-sm font-medium">Last 12 months</p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Preset */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wider text-foreground">
              Columns
            </Label>
            <RadioGroup
              value={preset}
              onValueChange={(v) => setPreset(v as ExportPreset)}
              className="space-y-2"
            >
              <Label
                htmlFor="preset-full"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="preset-full" value="full" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Full export</p>
                  <p className="text-xs text-muted-foreground">
                    Every column: order ID, payment method, imported flag,
                    timestamps. Best for backup / accounting.
                  </p>
                </div>
              </Label>
              <Label
                htmlFor="preset-reimport"
                className="flex items-center gap-3 rounded-md border border-input bg-card hover:bg-secondary cursor-pointer p-3 transition-colors"
              >
                <RadioGroupItem id="preset-reimport" value="reimportable" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Re-importable</p>
                  <p className="text-xs text-muted-foreground">
                    Only the columns the importer accepts, in the same shape
                    as the template. Round-trips cleanly back into Dibs.
                  </p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          <div className="rounded-md bg-secondary/50 px-3 py-2 text-sm">
            <span className="font-medium">{rowCount}</span> booking
            {rowCount === 1 ? "" : "s"} will be exported.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={downloading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={downloading || rowCount === 0}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Download {format.toUpperCase()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
